<template>
  <div>
    <div v-if="clipBoard" class="mb-3">
      <a :href="clipBoard" target="_blank"><app-icon name="clipboard"/>
        Open Endorsement </a>
    </div>

    <div v-if="steps.length > 0" class="timeline">
      <div v-for="(step, stepIndex) in steps" :key="step.index" class="timeline-item">
        <div class="timeline-content">
            <div class="d-flex justify-content-between align-items-center mb-2">
            <strong v-html="step.label"></strong>
            <div>
              <el-button size="mini" type="primary" @click.stop="startEditingStep(step)">Edit</el-button>
            </div>
          </div>

          <div v-for="child in step.children" :key="child.id" class="child-details mb-3">
            <div class="child-header" :class="{ 'active-header': child._expanded }" @click="toggleDetails(child)">
              <span class="view-details-text">View Details</span>
              <i :class="child._expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
            </div>
            <div class="d-flex align-items-center mt-2 mb-2" style="gap: 12px;">
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="remove(child)" title="Delete Action"></el-button>
              <span class="text-muted small"><strong>Action Date:</strong> {{ formatDateUK(child.created_at) }}</span>
            </div>

            <transition name="fade">
              <div v-show="child._expanded || child._editing" class="child-body" @click.stop>
                <div v-if="!child._editing">
                  <div class="view-only-card p-3 mb-2" style="background:#f7fbff;border:1px solid #e6f0ff;border-radius:6px;">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="text-muted small">Action Date:</div>
                      <div class="fw-bold small" @click.stop>{{ formatDateUK(child.created_at) }}</div>
                    </div>

                    <div class="mb-2">
                      <div v-html="child.action_comment" class="comment-html" style="white-space:pre-wrap;"></div>
                    </div>

                    <div v-if="child.applicant_files && child.applicant_files.length > 0" class="mt-2">
                      <div class="small text-muted mb-1">Files:</div>
                      <div class="d-flex flex-wrap" style="gap:8px;">
                        <a v-for="(file, idx) in child.applicant_files" :key="'viewfile-'+idx" :href="`/${file.fileURL}`" target="_blank" class="file-chip p-2" style="background:#fff;border:1px solid #e9eef6;border-radius:6px;color:#2b6cb0;text-decoration:none;display:flex;align-items:center;gap:8px;">
                          <i class="el-icon-document" style="font-size:18px;color:#2b6cb0"></i>
                          <span style="max-width:180px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ file.name }}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else>
                  <label class="fontWeight">Action Date:</label>
                  <app-input
                      v-model="child._editedDate"
                      type="date"
                      placeholder="Select Date"
                      class="w-100 mb-2">
                  </app-input>

                  <label class="fontWeight">Comment:</label>
                  <app-input
                      class="col-sm-12"
                      type="text-editor"
                      v-model="child._editedComment"
                      placeholder="Write Comment"
                      :required="true"
                  />
                  
                  <!-- Existing Files (Editable) -->
                  <div v-if="child.applicant_files && child.applicant_files.length > 0" class="mt-3">
                    <label class="fontWeight">Existing Files:</label>
                    <div v-for="(file, fileIndex) in child.applicant_files" :key="fileIndex" class="card mb-2 p-2" style="max-width: 300px; position: relative;">
                        <button type="button" class="close" aria-label="Close" style="position: absolute; top: 5px; right: 10px;" @click.prevent="removeFile(child, fileIndex, file.id)">
                        <span aria-hidden="true" style="color: red;">&times;</span>
                        </button>
                        <div class="d-flex align-items-center">
                        <div class="mr-3">
                            <a :href="`/${file.fileURL}`" target="_blank">
                                <i class="el-icon-document" style="font-size: 2rem;"></i>
                            </a>
                        </div>
                        <div>
                            <a :href="`/${file.fileURL}`" target="_blank" class="text-truncate d-block" style="max-width: 180px;" :title="file.name">{{ file.name }}</a>
                            <div class="text-success small">
                            <i class="el-icon-circle-check"></i> Uploaded
                            </div>
                        </div>
                        </div>
                    </div>
                  </div>

                  <!-- Newly Uploaded Files (Preview) -->
                  <div v-if="child._newlyUploadedFiles && child._newlyUploadedFiles.length > 0" class="mt-3">
                    <label class="fontWeight">Newly Uploaded Files:</label>
                    <div v-for="(file, fileIndex) in child._newlyUploadedFiles" :key="'new-'+fileIndex" class="card mb-2 p-2" style="max-width: 300px; position: relative;">
                        <button type="button" class="close" aria-label="Close" style="position: absolute; top: 5px; right: 10px;" @click.prevent="removeNewFile(child, fileIndex)">
                        <span aria-hidden="true" style="color: red;">&times;</span>
                        </button>
                        <div class="d-flex align-items-center">
                        <div class="mr-3">
                            <a :href="`/${file.fileURL}`" target="_blank">
                                <i class="el-icon-document" style="font-size: 2rem;"></i>
                            </a>
                        </div>
                        <div>
                            <a :href="`/${file.fileURL}`" target="_blank" class="text-truncate d-block" style="max-width: 180px;" :title="file.name">{{ file.name }}</a>
                            <div class="text-success small">
                            <i class="el-icon-circle-check"></i> Ready to Save
                            </div>
                        </div>
                        </div>
                    </div>
                  </div>

                  <div class="mt-3">
                    <label class="fontWeight">Add New Files:</label>
                    <multi-upload
                        :ref="'multiUpload-' + child.id"
                        :uploadedFile="child._newlyUploadedFiles"
                        :emailAddress="emailAddress"
                    />
                  </div>

                  <div class="d-flex mt-2" style="gap: 8px;">
                    <el-button size="mini" type="primary" @click="saveEdit(child)">Save</el-button>
                    <el-button size="mini" @click="cancelEdit(child)">Cancel</el-button>
                  </div>
                </div>
              </div>
            </transition>

            <div class="mt-2">
              <el-checkbox
                  v-model="child.include_in_report"
                  @change="checked => updateVisibility(child, checked)"
              >
                Add to Report
              </el-checkbox>
            </div>
            <div class="mt-2">
              <el-checkbox
                  v-model="child.screening_endorsement.auto_reminder_enabled"
                  @change="checked => toggleAutoReminder(child, checked)"
              >
                Auto Reminder
              </el-checkbox>
            </div>
            <div v-if="child.screening_endorsement && child.screening_endorsement.id" class="mt-2">

              <el-button size="mini"
                         type="success"
                         @click="copyToClipboard(generateCopyUrl(child))">
                Generate & Copy Endorsement Link
              </el-button>
            </div>

          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {Comment, File} from "@Views/Screening/utility/apiAgent";
import moment from "moment/moment";
import MultiUpload from "@Views/Screening/ResuableComponents/MultiUpload.vue";

export default {
  name: "CommentStepTimeline",
  components: {
    MultiUpload
  },
  props: {
    steps: Array,
    history: Object,
    emailAddress: String
  },
  data() {
    return {
      clipBoard: ''
    };
  },
  mounted() {
    this.initializeStepChildren();
    
    // Listen for data refresh events from other tabs/components
    this.$hub.$on('refresh-verification-steps', () => {
      this.$emit('refresh-data');
    });
    
    // Listen for checkbox updates from other instances
    this.$hub.$on('verification-checkbox-updated', (data) => {
      this.updateLocalCheckboxState(data);
    });
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$hub.$off('refresh-verification-steps');
    this.$hub.$off('verification-checkbox-updated');
  },
  watch: {
    steps: {
      handler(newSteps) {
        // Re-initialize children when steps prop changes (e.g., when switching pages)
        this.initializeStepChildren();
      },
      deep: true,
      immediate: false
    },
    // Watch for changes in comment data (when drawer opens with different history item)
    'history.id': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          // History item changed, re-initialize
          this.initializeStepChildren();
        }
      },
      immediate: false
    }
  },
  methods: {
    getApplicantId() {
      if (this.history && this.history.applicant_id) {
        return this.history.applicant_id;
      }
      try {
        const path = window.location.pathname || '';
        // Expecting /candidate-detail/{companyId}/{applicantId}
        const match = path.match(/candidate-detail\/\d+\/(\d+)/);
        if (match && match[1]) {
          return parseInt(match[1]);
        }
      } catch (e) {
        // ignore
      }
      return null;
    },
    formatDateUK(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : '';
    },
    async copyToClipboard(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch data: ${response.statusText}`);
        const text = await response.text();
        this.clipBoard = text;
        await navigator.clipboard.writeText(text);
        this.$message.success("Response copied to clipboard!");
      } catch (error) {
        this.$message.error(`Failed to copy response: ${error.message}`);
      }
    },
    toggleAutoReminder(child, checked) {
      // Optional: Optimistically update the UI
      child.screening_endorsement.auto_reminder_enabled = checked;
      axios.post('/screening-status/update-status-visibility', {
        nodeId: child.id,
        status: child,
        auto_reminder_enabled: checked
      }).then(response => {
        this.$message.success('Status updated successfully!');
        child.screening_endorsement.auto_reminder_enabled = checked;
        
        // Emit event to notify other components/tabs
        this.$hub.$emit('verification-checkbox-updated', {
          id: child.id,
          type: 'auto_reminder',
          value: checked
        });
        this.$hub.$emit('refresh-verification-steps');
      }).catch(error => {
        this.$message.error('Failed to update status.');
        console.error(error);
        // Revert on error
        child.screening_endorsement.auto_reminder_enabled = !checked;
      });
    },
    updateVisibility(data, checked) {
      // Optimistically update UI
      const previousValue = data.include_in_report;
      data.include_in_report = checked;
      
      axios.post('/screening-status/update-status-visibility', {
        nodeId: data.id,
        status: data,
        includeInReport: checked
      }).then(response => {
        this.$message.success('Status updated successfully!');
        this.$emit('comment-updated', {
          ...data,
          include_in_report: checked
        });
        
        // Emit event to notify other components/tabs about the change
        this.$hub.$emit('verification-checkbox-updated', {
          id: data.id,
          type: 'include_in_report',
          value: checked
        });
        this.$hub.$emit('refresh-verification-steps');
      }).catch(error => {
        this.$message.error('Failed to update status.');
        console.error(error);
        // Revert on error
        data.include_in_report = previousValue;
      });
    },
    updateLocalCheckboxState(updateData) {
      // Update checkbox state when notified by other instances
      this.steps.forEach(step => {
        step.children?.forEach(child => {
          if (child.id === updateData.id) {
            if (updateData.type === 'include_in_report') {
              this.$set(child, 'include_in_report', updateData.value);
            } else if (updateData.type === 'auto_reminder') {
              if (!child.screening_endorsement) {
                this.$set(child, 'screening_endorsement', {});
              }
              this.$set(child.screening_endorsement, 'auto_reminder_enabled', updateData.value);
            }
          }
        });
      });
    },
    remove(step) {
      this.$emit('node-delete', step);
    },
    removeFile(child, fileIndex, applicantFileId) {
      if (!window.confirm("Are you sure you want to delete this file?")) {
        return;
      }
      File.deleteDocument(applicantFileId)
          .then(({data}) => {
            child.applicant_files.splice(fileIndex, 1);
            this.$message.success(data.message);
          })
          .catch(error => {
            this.$message.error("Failed to delete file.");
            console.error(error);
          });
    },
    removeNewFile(child, fileIndex) {
      child._newlyUploadedFiles.splice(fileIndex, 1);
    },
    toggleDetails(child) {
      // If the child is currently being edited, do not toggle details
      if (child._editing) return;

      // Ensure _expanded is defined and toggle reactively
      if (typeof child._expanded === 'undefined') {
        this.$set(child, '_expanded', true);
      } else {
        this.$set(child, '_expanded', !child._expanded);
      }

      // If we're expanding the details view, ensure edit mode is turned off
      if (child._expanded) {
        this.$set(child, '_editing', false);
      }
    },
    shouldShowCopyButton(child) {
      return (
          this.history?.screening_endorsement &&
          this.history.screening_endorsement.endorseable_id === child?.id
      );
    },
    generateCopyUrl(screeningStatus) {
      return `/screening-endorsement/link/${screeningStatus.screening_endorsement.id}`;
    },
    startEditingStep(step) {
      // If any child in this step is already in edit mode, treat this as a toggle
      const anyEditing = step.children?.some(child => child._editing === true);
      if (anyEditing) {
        step.children?.forEach(child => {
          this.cancelEdit(child);
        });
        return;
      }

      step.children?.forEach(child => {
        // Enter edit mode and ensure the header is NOT highlighted
        this.$set(child, '_editing', true);
        this.$set(child, '_expanded', false);

        if (typeof child._editedComment === 'undefined') this.$set(child, '_editedComment', child.action_comment);
        if (typeof child._editedDate === 'undefined') this.$set(child, '_editedDate', child.created_at);
        if (typeof child._newlyUploadedFiles === 'undefined') this.$set(child, '_newlyUploadedFiles', []);
      });
    },
    async saveEdit(child) {
      if (child._editedComment !== '') {
        const refName = 'multiUpload-' + child.id;
        const uploadComp = this.$refs[refName] ? this.$refs[refName][0] : null;
        if (uploadComp) {
            await uploadComp.submitUpload();
        }

        const newIds = (child._newlyUploadedFiles || []).map(f => f.id);
        
        let allFileIds = [];
        let filesToDelete = [];

        if (newIds.length > 0) {
          // If new files are uploaded, use ONLY them (replace logic)
          allFileIds = [...newIds];
          // Mark existing files for deletion
          if (child.applicant_files && child.applicant_files.length > 0) {
            filesToDelete = [...child.applicant_files];
          }
        } else {
          // If no new files, keep existing ones
          const existingIds = (child.applicant_files || []).map(f => f.id);
          allFileIds = [...existingIds];
        }

        const commentObj = {
          id: child.id,
          action_comment: child._editedComment,
          status: child.type, // Note: original code used child.type for 'status', assume correct
          statusable_type: child.statusable_type,
          statusable_id: child.statusable_id,
          applicant_files: allFileIds,
          created_at: child._editedDate || child.created_at
        };

        const applicantId = this.getApplicantId();
        if (!applicantId) {
          this.$message.error('Applicant not identified for update.');
          return;
        }

        Comment.editComment(applicantId, child.id, commentObj)
            .then(({ data }) => {
              // Update local action comment and date
              child.action_comment = child._editedComment;
              child.created_at = child._editedDate || child.created_at;
              
              // If we replaced files, delete the old ones now
              if (filesToDelete.length > 0) {
                filesToDelete.forEach(file => {
                  File.deleteDocument(file.id).catch(e => console.error(e));
                });
              }

              // Update UI with new files
              if (newIds.length > 0) {
                this.$set(child, 'applicant_files', [...child._newlyUploadedFiles]);
                this.$set(child, '_newlyUploadedFiles', []);
              }
              
              // Ensure edit mode is turned off reactively
              this.$set(child, '_editing', false);
              this.$message.success(data.message || 'Comment updated successfully.');
              
              // Emit event to parent component to refresh data
              this.$emit('comment-updated', child);
              
              // Emit event to notify other components/tabs about the update
              this.$hub.$emit('refresh-verification-steps');
            })
            .catch(error => {
              this.$message.error('Failed to update comment.');
              console.error(error);
            });
      }
    },

    cancelEdit(child) {
      // Collapse the edit UI and reset edited fields (use $set for reactivity)
      this.$set(child, '_editing', false);
      this.$set(child, '_expanded', false);
      this.$set(child, '_editedComment', child.action_comment);
      this.$set(child, '_editedDate', child.created_at);
      this.$set(child, '_newlyUploadedFiles', []);

      // Notify any multi-upload components to clear their internal file lists
      if (this.$hub && this.$hub.$emit) {
        this.$hub.$emit('clear-uploaded-files');
      }

      // Remove focus to ensure any open datepicker/popover closes
      try {
        if (document && document.activeElement && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
      } catch (e) {
        // ignore
      }
      // Also forcibly hide any open Element UI date picker panels or other popovers
      try {
        // Element UI date picker panels
        const panels = document.querySelectorAll('.el-picker-panel');
        panels.forEach(p => p.style.display = 'none');

        // vue-date-picker / vc-popover style popovers
        const vcPop = document.querySelectorAll('.vc-popover, .vdp-popup, .v-date-picker__popover');
        vcPop.forEach(p => p.style.display = 'none');

        // Summernote / text-editor popovers
        const notePop = document.querySelectorAll('.note-popover, .note-editor .note-toolbar');
        notePop.forEach(p => p.style.display = 'none');
      } catch (e) {
        // ignore
      }
    },
    initializeStepChildren() {
      this.steps.forEach(step => {
        step.children?.forEach(child => {
          // Only initialize if not already initialized (to preserve existing state during updates)
          if (typeof child._expanded === 'undefined') {
            this.$set(child, '_expanded', false);
          }
          if (typeof child._editing === 'undefined') {
            this.$set(child, '_editing', false);
          }
          if (typeof child._editedComment === 'undefined') {
            this.$set(child, '_editedComment', child.action_comment);
          }
          if (typeof child._editedDate === 'undefined') {
            this.$set(child, '_editedDate', child.created_at);
          }
          if (typeof child._newlyUploadedFiles === 'undefined') {
            this.$set(child, '_newlyUploadedFiles', []);
          }
          if (typeof child.screening_endorsement === 'undefined') {
            this.$set(child, 'screening_endorsement', {});
          }
          
          // Always sync the checkbox values from server data
          this.$set(child, 'include_in_report', child.include_in_report ?? false);
          if (child.screening_endorsement) {
            this.$set(child.screening_endorsement, 'auto_reminder_enabled', 
              child.screening_endorsement.auto_reminder_enabled ?? false);
          }
        });
      });
    },





  },

};
</script>

<style scoped>
.timeline {
  position: relative;
  margin: 2rem 0;
  padding-left: 1rem;
  border-left: 2px solid #dee2e6;
}
.timeline-item {
  position: relative;
  margin-bottom: 2rem;
}
.timeline-content {
  background: #f8f9fa;
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  border-radius: 0.5rem;
  box-shadow: 0 0 5px rgba(0,0,0,0.05);
}
.child-details {
  background: #ffffff;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 0 3px rgba(0,0,0,0.1);
  width: 100%;
  overflow: hidden;
  transition: all 0.3s ease;
}

.child-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  padding: 10px 15px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: 6px;
  color: #475569;
  margin-bottom: 8px;
}

.child-header:hover {
  background-color: #e2e8f0;
  color: #2563eb;
  border-color: #cbd5e1;
}

.active-header {
  background-color: #2563eb !important;
  color: #ffffff !important;
  border-color: #1d4ed8 !important;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
}

.view-details-text {
  font-size: 0.9rem;
  letter-spacing: 0.025em;
}

.child-body {
  margin-top: 10px;
  transition: all 0.3s ease;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

</style>
